import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  approveStockCount,
  getStockCount,
  getStockCountCategoryOptions,
  getStockCountItems,
  updateStockCountItemAdjustment,
} from "../api/stockCountsApi";
import ApproveStockCountDialog from "../components/ApproveStockCountDialog";
import EditStockCountAdjustmentDialog from "../components/EditStockCountAdjustmentDialog";
import StockCountCardShell from "../components/StockCountCardShell";
import StockCountDetailActions from "../components/StockCountDetailActions";
import StockCountDetailHeader from "../components/StockCountDetailHeader";
import StockCountItemsFilters from "../components/StockCountItemsFilters";
import StockCountItemsTable from "../components/StockCountItemsTable";
import { getStockCountErrorMessage } from "../utils/stockCountError";
import {
  normalizeStockCountItemList,
  normalizeStockCountCategories,
  STOCK_COUNT_ITEM_TYPE,
} from "../utils/stockCountItemModel";
import {
  normalizeStockCount,
  STOCK_COUNT_STATUS,
} from "../utils/stockCountModel";

const LIST_PATH = "/stock/operacion/conteo-de-stock";
const SEARCH_DELAY_MS = 300;

function unwrapHttp(response) {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    !Array.isArray(response.data) &&
    !("meta" in response)
  ) {
    return response.data;
  }
  return response;
}

function collectLineIdsFromPayload(value, lineIds = new Set(), assumeConflict = false) {
  if (!value || typeof value !== "object") return lineIds;

  if (Array.isArray(value)) {
    value.forEach((item) =>
      collectLineIdsFromPayload(item, lineIds, assumeConflict)
    );
    return lineIds;
  }

  const rawLineId = value.id ?? value.lineId;
  const lineId = Number(rawLineId);
  if (
    Number.isFinite(lineId) &&
    (assumeConflict || value.hasStockConflict === true || value.stockConflict === true)
  ) {
    lineIds.add(lineId);
  }

  ["conflicts", "lines", "items", "stockConflicts"].forEach((key) => {
    if (key in value) collectLineIdsFromPayload(value[key], lineIds, true);
  });

  return lineIds;
}

function getApprovalConflictLineIds(error) {
  const status = Number(error?.response?.status || error?.status || 0);
  if (status !== 409) return [];

  const payload =
    error?.response?.data ||
    error?.data ||
    error?.response ||
    error;

  return Array.from(collectLineIdsFromPayload(payload));
}

export default function StockCountDetailPage() {
  const { stockCountId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const listPage = location.state?.page || 1;

  const latestHeaderRequestId = useRef(0);
  const latestItemsRequestId = useRef(0);
  const operationRef = useRef(false);

  const [stockCount, setStockCount] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [itemType, setItemType] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [sector, setSector] = useState("");
  const [adjustmentType, setAdjustmentType] = useState("");
  const [viewMode, setViewMode] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasGlobalStockConflicts, setHasGlobalStockConflicts] = useState(false);
  const [approvalConflictLineIds, setApprovalConflictLineIds] = useState(
    () => new Set()
  );

  const [headerLoading, setHeaderLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  const [headerError, setHeaderError] = useState("");
  const [itemsError, setItemsError] = useState("");
  const [categoriesError, setCategoriesError] = useState("");
  const [operationError, setOperationError] = useState("");
  const [operationSuccess, setOperationSuccess] = useState("");

  const [headerReloadVersion, setHeaderReloadVersion] = useState(0);
  const [itemsReloadVersion, setItemsReloadVersion] = useState(0);
  const [categoriesReloadVersion, setCategoriesReloadVersion] = useState(0);

  const [editingItem, setEditingItem] = useState(null);
  const [adjustmentSaving, setAdjustmentSaving] = useState(false);
  const [adjustmentError, setAdjustmentError] = useState("");

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState("");

  const readOnly = stockCount?.status !== STOCK_COUNT_STATUS.REALIZED;
  const canApprove = stockCount?.status === STOCK_COUNT_STATUS.REALIZED;

  function goBack() {
    navigate(LIST_PATH, { state: { page: listPage } });
  }

  useEffect(() => {
    const timeout = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [itemType, categoryId, sector, adjustmentType, viewMode, search]);

  function handleItemTypeChange(nextItemType) {
    setItemType(nextItemType);
    setCategoryId("");
    setSector("");
  }

  const fetchHeader = useCallback(
    async (signal) => {
      const requestId = ++latestHeaderRequestId.current;
      setHeaderLoading(true);
      setHeaderError("");
      try {
        const response = await getStockCount(stockCountId, { signal });
        if (signal?.aborted || requestId !== latestHeaderRequestId.current) return;
        setStockCount(normalizeStockCount(unwrapHttp(response)));
      } catch (error) {
        if (signal?.aborted || requestId !== latestHeaderRequestId.current) return;
        setStockCount(null);
        setHeaderError(
          getStockCountErrorMessage(error, "No se pudo cargar el conteo de stock.")
        );
      } finally {
        if (!signal?.aborted && requestId === latestHeaderRequestId.current) {
          setHeaderLoading(false);
        }
      }
    },
    [stockCountId]
  );

  const hasActiveFilters = Boolean(
    viewMode === "differences" ||
      itemType ||
      adjustmentType ||
      search ||
      categoryId ||
      sector
  );

  const fetchItems = useCallback(
    async (signal) => {
      const requestId = ++latestItemsRequestId.current;
      setItemsLoading(true);
      setItemsError("");
      try {
        const response = await getStockCountItems(stockCountId, {
          page,
          itemType: itemType || undefined,
          categoryId:
            itemType === STOCK_COUNT_ITEM_TYPE.PURCHASE_ITEM
              ? categoryId || undefined
              : undefined,
          sector:
            itemType === STOCK_COUNT_ITEM_TYPE.PREPARED_PRODUCT
              ? sector || undefined
              : undefined,
          adjustmentType: adjustmentType || undefined,
          withAdjustments:
            viewMode === "differences"
              ? true
              : undefined,
          search: search || undefined,
          signal,
        });
        if (signal?.aborted || requestId !== latestItemsRequestId.current) return;

        const result = normalizeStockCountItemList(unwrapHttp(response));
        if (result.meta.totalPages > 0 && page > result.meta.totalPages) {
          setPage(result.meta.totalPages);
          return;
        }
        setItems(result.data);
        setTotalItems(result.meta.totalItems);
        setTotalPages(result.meta.totalPages);
        setHasGlobalStockConflicts(result.meta.hasStockConflicts);
        if (!result.meta.hasStockConflicts) {
          setApprovalConflictLineIds(new Set());
        }
      } catch (error) {
        if (signal?.aborted || requestId !== latestItemsRequestId.current) return;
        setItems([]);
        setTotalItems(0);
        setTotalPages(0);
        setItemsError(
          getStockCountErrorMessage(
            error,
            "No se pudieron cargar los productos del conteo."
          )
        );
      } finally {
        if (!signal?.aborted && requestId === latestItemsRequestId.current) {
          setItemsLoading(false);
        }
      }
    },
    [
      stockCountId,
      page,
      itemType,
      categoryId,
      sector,
      adjustmentType,
      viewMode,
      search,
    ]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchHeader(controller.signal);
    return () => controller.abort();
  }, [fetchHeader, headerReloadVersion]);

  useEffect(() => {
    const controller = new AbortController();
    fetchItems(controller.signal);
    return () => controller.abort();
  }, [fetchItems, itemsReloadVersion]);

  useEffect(() => {
    const controller = new AbortController();
    async function fetchCategories() {
      setCategoriesLoading(true);
      setCategoriesError("");
      try {
        const response = await getStockCountCategoryOptions({
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setCategories(normalizeStockCountCategories(unwrapHttp(response)));
      } catch (error) {
        if (controller.signal.aborted) return;
        setCategories([]);
        setCategoriesError(
          getStockCountErrorMessage(
            error,
            "No se pudieron cargar las categorías."
          )
        );
      } finally {
        if (!controller.signal.aborted) setCategoriesLoading(false);
      }
    }
    fetchCategories();
    return () => controller.abort();
  }, [categoriesReloadVersion]);

  const pageSummary = useMemo(() => {
    if (!totalItems) return "Sin productos para mostrar";
    return `Mostrando ${items.length} de ${totalItems} productos`;
  }, [items.length, totalItems]);

  const statusDescription = useMemo(() => {
    if (stockCount?.status === STOCK_COUNT_STATUS.APPROVED) {
      return "Este conteo ya fue aprobado e integrado. La lista se muestra en modo de solo lectura.";
    }
    if (stockCount?.status === STOCK_COUNT_STATUS.CANCELLED) {
      return "Este conteo fue cancelado. La lista se muestra en modo de solo lectura.";
    }
    if (stockCount?.status === STOCK_COUNT_STATUS.EXPIRED) {
      return "La solicitud de conteo está vencida. Se muestra la información parcial disponible en modo de solo lectura.";
    }
    if (stockCount?.status === STOCK_COUNT_STATUS.REALIZED) {
      return "El conteo fue realizado y está listo para verificar, aprobar e integrar en stock.";
    }
    if (stockCount?.status === STOCK_COUNT_STATUS.IN_PROGRESS) {
      return "El conteo está en curso. Se muestra la información parcial disponible.";
    }
    return "El conteo todavía no fue realizado. Se muestra la información parcial disponible en modo de solo lectura.";
  }, [stockCount?.status]);

  const emptyItemsMessage = hasActiveFilters
    ? "No hay productos que coincidan con los filtros seleccionados."
    : readOnly
      ? "No hay información de productos contados para mostrar."
      : "Todavía no hay productos contados para este conteo.";

  const hasVisibleStockConflicts = useMemo(
    () =>
      items.some(
        (item) => item.hasStockConflict || approvalConflictLineIds.has(item.id)
      ),
    [items, approvalConflictLineIds]
  );
  const hasBlockingStockConflicts =
    hasGlobalStockConflicts ||
    hasVisibleStockConflicts ||
    approvalConflictLineIds.size > 0;

  const tableItems = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        hasStockConflict:
          Boolean(item.hasStockConflict) || approvalConflictLineIds.has(item.id),
      })),
    [items, approvalConflictLineIds]
  );

  function handleEditItem(item) {
    if (readOnly || operationRef.current) return;
    setAdjustmentError("");
    setEditingItem(item);
  }

  function closeEditDialog() {
    if (adjustmentSaving) return;
    setAdjustmentError("");
    setEditingItem(null);
  }

  async function handleSaveAdjustment(item, adjustmentValue) {
    if (operationRef.current || !item?.id) return;
    operationRef.current = true;
    setAdjustmentSaving(true);
    setAdjustmentError("");
    setApproveError("");
    setOperationError("");
    setOperationSuccess("");
    try {
      const response = await updateStockCountItemAdjustment(
        stockCountId,
        item.id,
        { adjustment: adjustmentValue }
      );
      unwrapHttp(response);
      setEditingItem(null);
      setApproveError("");
      setOperationError("");
      setItemsReloadVersion((current) => current + 1);
    } catch (error) {
      setAdjustmentError(
        getStockCountErrorMessage(
          error,
          "No se pudo guardar el ajuste manual del producto."
        )
      );
    } finally {
      operationRef.current = false;
      setAdjustmentSaving(false);
    }
  }

  function openApproveDialog() {
    if (!canApprove || hasBlockingStockConflicts || operationRef.current) return;
    setApproveError("");
    setOperationSuccess("");
    setApproveDialogOpen(true);
  }

  function closeApproveDialog() {
    if (approving) return;
    setApproveError("");
    setApproveDialogOpen(false);
  }

  async function handleApprove() {
    if (!canApprove || hasBlockingStockConflicts || operationRef.current) return;
    operationRef.current = true;
    setApproving(true);
    setApproveError("");
    setOperationError("");
    setOperationSuccess("");
    try {
      const response = await approveStockCount(stockCountId);
      setStockCount(normalizeStockCount(unwrapHttp(response)));
      setApproveDialogOpen(false);
      setApprovalConflictLineIds(new Set());
      setHasGlobalStockConflicts(false);
      setOperationSuccess("Conteo aprobado e integrado en stock.");
      setHeaderReloadVersion((current) => current + 1);
      setItemsReloadVersion((current) => current + 1);
    } catch (error) {
      const message = getStockCountErrorMessage(
        error,
        "No se pudo aprobar e integrar el conteo."
      );
      const conflictLineIds = getApprovalConflictLineIds(error);
      if (conflictLineIds.length > 0) {
        setApprovalConflictLineIds((current) => {
          const next = new Set(current);
          conflictLineIds.forEach((lineId) => next.add(lineId));
          return next;
        });
        setHasGlobalStockConflicts(true);
      }
      setApproveError(message);
      setOperationError(message);
      setItemsReloadVersion((current) => current + 1);
    } finally {
      operationRef.current = false;
      setApproving(false);
    }
  }

  if (headerLoading) {
    return (
      <Stack spacing={2} sx={{ p: 2, flex: 1, minHeight: 0 }}>
        <StockCountDetailHeader stockCount={null} onBack={goBack} />
        <StockCountCardShell>
          <Stack sx={{ minHeight: 280, alignItems: "center", justifyContent: "center" }}>
            <CircularProgress />
            <Typography sx={{ mt: 1.5, color: "primary.900", fontWeight: 700 }}>
              Cargando lista de conteo...
            </Typography>
          </Stack>
        </StockCountCardShell>
      </Stack>
    );
  }

  if (headerError || !stockCount) {
    return (
      <Stack spacing={2} sx={{ p: 2, flex: 1 }}>
        <StockCountDetailHeader stockCount={null} onBack={goBack} />
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              onClick={() => setHeaderReloadVersion((current) => current + 1)}
            >
              Reintentar
            </Button>
          }
        >
          {headerError || "No se encontró el conteo de stock."}
        </Alert>
        <Button onClick={goBack} sx={{ alignSelf: "flex-start" }}>
          Volver al listado
        </Button>
      </Stack>
    );
  }

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        flex: 1,
        minHeight: 0,
        overflow: { xs: "auto", md: "hidden" },
      }}
    >
      <StockCountDetailHeader
        stockCount={stockCount}
        statusDescription={statusDescription}
        onBack={goBack}
      />

      <StockCountDetailActions
        canApprove={canApprove}
        approveDisabled={hasBlockingStockConflicts}
        approving={approving}
        disabled={adjustmentSaving}
        onApprove={openApproveDialog}
      />

      <StockCountItemsFilters
        itemType={itemType}
        categoryId={categoryId}
        sector={sector}
        categories={categories}
        categoriesLoading={categoriesLoading}
        adjustmentType={adjustmentType}
        viewMode={viewMode}
        search={searchInput}
        disabled={adjustmentSaving || approving}
        onItemTypeChange={handleItemTypeChange}
        onCategoryChange={setCategoryId}
        onSectorChange={setSector}
        onAdjustmentTypeChange={setAdjustmentType}
        onViewModeChange={setViewMode}
        onSearchChange={setSearchInput}
      />

      {operationError ? (
        <Alert
          severity="error"
          onClose={() => setOperationError("")}
          sx={{ borderRadius: "16px", fontWeight: 650 }}
        >
          {operationError}
        </Alert>
      ) : null}

      {operationSuccess ? (
        <Alert
          severity="success"
          onClose={() => setOperationSuccess("")}
          sx={{ borderRadius: "16px", fontWeight: 650 }}
        >
          {operationSuccess}
        </Alert>
      ) : null}

      {hasBlockingStockConflicts ? (
        <Alert severity="warning" sx={{ borderRadius: "16px", fontWeight: 650 }}>
          Hay ajustes que dejarían el stock por debajo de cero. Corregí esos
          conflictos antes de aprobar e integrar el conteo.
        </Alert>
      ) : null}

      {categoriesError && itemType === STOCK_COUNT_ITEM_TYPE.PURCHASE_ITEM ? (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setCategoriesReloadVersion((current) => current + 1)}
            >
              Reintentar
            </Button>
          }
          sx={{ borderRadius: "16px" }}
        >
          {categoriesError}
        </Alert>
      ) : null}

      {itemsError ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setItemsReloadVersion((current) => current + 1)}
            >
              Reintentar
            </Button>
          }
          sx={{ borderRadius: "16px" }}
        >
          {itemsError}
        </Alert>
      ) : null}

      <StockCountCardShell sx={{ p: 0, overflow: "hidden" }}>
        <Stack spacing={0} sx={{ height: "100%", minHeight: 0 }}>
          <StockCountItemsTable
            rows={tableItems}
            loading={itemsLoading}
            readOnly={readOnly}
            editingLineId={editingItem?.id ?? null}
            onEdit={handleEditItem}
            emptyMessage={emptyItemsMessage}
          />

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.2}
            sx={{
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              px: 2,
              py: 1.25,
              borderTop: "1px solid rgba(0,0,0,.05)",
              bgcolor: "common.white",
            }}
          >
            <Typography sx={{ color: "primary.900", fontSize: 13, fontWeight: 700 }}>
              {pageSummary}
            </Typography>
            <Pagination
              page={page}
              count={Math.max(totalPages, 1)}
              onChange={(_, value) => setPage(value)}
              shape="rounded"
              color="primary"
              disabled={itemsLoading || totalPages <= 1}
            />
          </Stack>
        </Stack>
      </StockCountCardShell>

      <EditStockCountAdjustmentDialog
        open={Boolean(editingItem)}
        item={editingItem}
        loading={adjustmentSaving}
        error={adjustmentError}
        onClose={closeEditDialog}
        onSave={handleSaveAdjustment}
      />

      <ApproveStockCountDialog
        open={approveDialogOpen}
        loading={approving}
        disabled={hasBlockingStockConflicts}
        error={approveError}
        onClose={closeApproveDialog}
        onConfirm={handleApprove}
      />
    </Stack>
  );
}
